import { z } from "zod";

const templateOutput = {
  "square-announcement": "square",
  "square-product": "square",
  "wide-launch": "wide",
  "wide-hero": "wide"
} as const;

const outputSchema = z.enum(["square", "wide"]);
const templateSchema = z.enum([
  "square-announcement",
  "square-product",
  "wide-launch",
  "wide-hero"
]);
const paletteSchema = z.enum(["noir", "ember", "violet", "cobalt", "bone"]);
const textSchema = z.object({
  eyebrow: z.string().max(100),
  headline: z.string().min(1).max(180),
  subline: z.string().max(220),
  cta: z.string().max(80),
  align: z.enum(["left", "center", "right"]),
  positionX: z.number().min(0.05).max(0.95),
  positionY: z.number().min(0.05).max(0.85),
  width: z.number().min(0.25).max(0.9),
  tone: z.enum(["light", "dark"])
});
const styleFields = {
  palette: paletteSchema,
  variance: z.number().min(0).max(1),
  vibrance: z.number().min(0.4).max(1.3),
  brightness: z.number().min(0.55).max(1.25),
  grain: z.number().min(0).max(0.35),
  bokeh: z.number().min(0).max(1),
  vignette: z.number().min(0).max(0.8)
};
const animationFields = {
  duration: z.literal(10),
  fps: z.literal(60),
  colorDrift: z.number().min(0).max(1),
  bokehDrift: z.number().min(0).max(1),
  shapeDrift: z.number().min(0).max(1)
};
const typographySchema = z.object({
  displayFont: z.enum(["staatliches", "bebas-neue", "archivo-black"]),
  bodyFont: z.enum(["lato", "barlow"])
});

function validateTemplateOutput(
  state: { template: keyof typeof templateOutput; output: "square" | "wide" },
  context: z.RefinementCtx
): void {
  if (templateOutput[state.template] !== state.output) {
    context.addIssue({
      code: "custom",
      path: ["output"],
      message: "Output size must match the selected template."
    });
  }
}

export const versionTwoStudioStateSchema = z
  .object({
    version: z.literal(2),
    seed: z.number().int().min(0).max(0xffffffff),
    output: outputSchema,
    template: templateSchema,
    text: textSchema,
    style: z.object({
      ...styleFields,
      motif: z.enum(["mesh", "bokeh", "arches", "light-columns", "orbital-halo"])
    }),
    animation: z.object(animationFields)
  })
  .superRefine(validateTemplateOutput);

export const studioStateSchema = z
  .object({
    version: z.literal(3),
    seed: z.number().int().min(0).max(0xffffffff),
    output: outputSchema,
    template: templateSchema,
    text: textSchema,
    typography: typographySchema,
    style: z.object({
      ...styleFields,
      motif: z.enum(["mesh", "bokeh", "arches", "light-columns", "orbital-halo"])
    }),
    animation: z.object(animationFields)
  })
  .superRefine(validateTemplateOutput);

export const legacyStudioStateSchema = z
  .object({
    version: z.literal(1),
    seed: z.number().int().min(0).max(0xffffffff),
    output: outputSchema,
    template: templateSchema,
    text: textSchema,
    style: z.object({
      ...styleFields,
      motif: z.enum(["mesh", "bokeh", "arches", "signal-field", "orbital-halo"])
    }),
    signature: z.object({
      amplitude: z.number().min(0.08).max(0.6),
      frequency: z.number().min(0.5).max(2.5),
      position: z.number().min(0.15).max(0.85),
      thickness: z.number().min(0.004).max(0.03),
      opacity: z.number().min(0.2).max(1),
      notation: z.boolean()
    }),
    animation: z.object({
      ...animationFields,
      waveformMotion: z.number().min(0).max(1)
    })
  })
  .superRefine(validateTemplateOutput);

export const savedPresetSchema = z.object({
  id: z.string().min(1).max(80),
  name: z.string().min(1).max(60),
  createdAt: z.string(),
  state: studioStateSchema
});
export const savedPresetListSchema = z.array(savedPresetSchema).max(30);

export type StudioState = z.infer<typeof studioStateSchema>;
export type VersionTwoStudioState = z.infer<typeof versionTwoStudioStateSchema>;
export type LegacyStudioState = z.infer<typeof legacyStudioStateSchema>;
export type SavedPreset = z.infer<typeof savedPresetSchema>;
export type OutputSize = StudioState["output"];
export type TemplateId = StudioState["template"];
export type PaletteId = StudioState["style"]["palette"];
export type MotifId = StudioState["style"]["motif"];
export type TextAlign = StudioState["text"]["align"];
export type TextTone = StudioState["text"]["tone"];
export type DisplayFontId = StudioState["typography"]["displayFont"];
export type BodyFontId = StudioState["typography"]["bodyFont"];

export interface RenderResult {
  overflow: boolean;
  headlineLines: string[];
  headlineSize: number;
  sampledLuminance: number;
}

export interface ExportProgress {
  phase: "preparing" | "rendering" | "finalizing" | "done";
  currentFrame: number;
  totalFrames: number;
  format: "mp4" | "webm";
}

export interface VideoCapability {
  format: "mp4" | "webm" | null;
  codec: "avc" | "vp9" | "vp8" | "media-recorder" | null;
  label: string;
}
